import { useState, useCallback, useMemo, useRef } from 'react';
import { X, MessageSquare, Phone } from 'lucide-react';
import { cn } from '@/lib/utils';
import { cleanSenderLabel } from '@/lib/utils/format';
import { WhatsAppIcon } from '@/components/icons/whatsapp-icon';
import { Button } from '@/components/ui/button';
import { Select } from '@/components/ui/select';
import { useSettingsStore } from '@/stores/settings-store';
import { applyCountryCode } from '@/lib/utils/phone';
import { MultiCountryWarning } from '@/components/common/multi-country-warning';
import type { Channel, Sender } from '@/types/messaging';

interface NewChatDialogProps {
  senders: Sender[];
  defaultSenderId?: string;
  onStart: (phone: string, channel: Channel, senderId: string) => void;
  onClose: () => void;
}

export function NewChatDialog({ senders, defaultSenderId, onStart, onClose }: NewChatDialogProps) {
  const [phone, setPhone] = useState('');
  const [selectedSenderId, setSelectedSenderId] = useState(
    defaultSenderId && senders.some((s) => s.id === defaultSenderId)
      ? defaultSenderId
      : senders[0]?.id || ''
  );
  const [showCountryCodeInput, setShowCountryCodeInput] = useState(false);
  const countryCodeRef = useRef<HTMLInputElement>(null);

  const preferences = useSettingsStore((s) => s.preferences);
  const updatePreferences = useSettingsStore((s) => s.updatePreferences);
  const defaultCountryCode = preferences.defaultCountryCode || '';

  const selectedSender = senders.find((s) => s.id === selectedSenderId);
  const channel: Channel = selectedSender?.channel === 'MMS' ? 'SMS' : (selectedSender?.channel || 'SMS');

  const senderOptions = useMemo(() =>
    senders.map((s) => {
      const IconComp = s.channel === 'WHATSAPP' ? WhatsAppIcon : MessageSquare;
      const iconColor = s.channel === 'WHATSAPP' ? 'text-green-500' : 'text-blue-500';
      return {
        value: s.id,
        label: cleanSenderLabel(s.label),
        description: s.phoneNumber || s.channel,
        icon: <IconComp className={`h-4 w-4 ${iconColor}`} />,
      };
    }),
    [senders]
  );

  const handleStart = useCallback(() => {
    const withCode = applyCountryCode(phone, defaultCountryCode);
    const cleaned = withCode.replace(/[^\d+]/g, '');
    if (cleaned.replace(/\D/g, '').length < 7 || !selectedSenderId) return;
    onStart(cleaned, channel, selectedSenderId);
  }, [phone, channel, selectedSenderId, onStart, defaultCountryCode]);

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleStart();
    }
    if (e.key === 'Escape') {
      onClose();
    }
  };

  const isValid = phone.replace(/\D/g, '').length >= 7 && !!selectedSenderId;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/50 backdrop-blur-sm"
        onClick={onClose}
      />

      {/* Dialog */}
      <div className="relative z-10 w-full max-w-sm rounded-xl border border-border bg-card shadow-xl mx-4">
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-border">
          <h3 data-testid="new-conversation-heading" className="text-sm font-semibold text-foreground">New Conversation</h3>
          <button
            onClick={onClose}
            className="rounded-lg p-1.5 text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        {/* Body */}
        <div className="px-5 py-4 space-y-4">
          {/* Sender selector */}
          <div>
            <label className="text-xs font-medium text-muted-foreground mb-2 block">From</label>
            <Select
              value={selectedSenderId}
              onValueChange={setSelectedSenderId}
              options={senderOptions}
              placeholder="Select sender"
              searchable={senderOptions.length > 5}
            />
          </div>

          {/* Phone number input */}
          <div className="space-y-2">
            <label className="text-xs font-medium text-muted-foreground block">To</label>
            <input
              data-testid="new-chat-phone-input"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="e.g. +1234567890"
              className="w-full h-10 rounded-lg border border-input bg-background px-3 text-sm outline-none transition-colors focus:border-primary focus:ring-2 focus:ring-primary/20"
              autoFocus
            />

            {/* KAN-125: number typed while multi-country is on → warn (see compose) */}
            {preferences.multiCountryContacts && phone.trim().length > 0 && (
              <MultiCountryWarning
                onTurnedOff={() => {
                  setShowCountryCodeInput(true);
                  requestAnimationFrame(() => countryCodeRef.current?.focus());
                }}
              />
            )}

            {/* Default country code — right aligned below input */}
            <div className="flex items-center justify-end gap-1.5">
              <span className="text-xs text-muted-foreground whitespace-nowrap">Default country code</span>
              {showCountryCodeInput ? (
                <div className="inline-flex items-center gap-0.5 rounded-md border border-primary bg-primary/5 px-2 py-0.5">
                  <span className="text-xs font-medium text-primary">+</span>
                  <input
                    ref={countryCodeRef}
                    value={defaultCountryCode.replace(/^\+/, '')}
                    onChange={(e) => {
                      const val = e.target.value.replace(/\D/g, '').slice(0, 4);
                      updatePreferences({ defaultCountryCode: val });
                    }}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === 'Escape') {
                        setShowCountryCodeInput(false);
                      }
                    }}
                    onBlur={() => setShowCountryCodeInput(false)}
                    placeholder="91"
                    className="w-10 bg-transparent text-xs font-medium outline-none text-primary placeholder:text-primary/40"
                    autoFocus
                  />
                </div>
              ) : (
                <button
                  type="button"
                  data-testid="new-chat-country-code-chip"
                  onClick={() => {
                    setShowCountryCodeInput(true);
                    requestAnimationFrame(() => countryCodeRef.current?.focus());
                  }}
                  disabled={!!preferences.multiCountryContacts}
                  title={preferences.multiCountryContacts ? 'Disabled while "Multi-country contacts" is on (see Compose View settings)' : undefined}
                  className={cn(
                    'inline-flex items-center gap-1 rounded-md border px-2 py-0.5 text-xs font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed',
                    defaultCountryCode
                      ? 'border-primary/30 bg-primary/5 text-primary hover:bg-primary/10'
                      : 'border-dashed border-muted-foreground/40 text-muted-foreground hover:border-primary hover:text-primary hover:bg-primary/5'
                  )}
                >
                  {defaultCountryCode ? `+${defaultCountryCode.replace(/^\+/, '')}` : 'Not set'}
                </button>
              )}
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-2 px-5 py-4 border-t border-border">
          <Button
            variant="outline"
            size="sm"
            onClick={onClose}
            className="h-9 text-sm px-4"
          >
            Cancel
          </Button>
          <Button
            data-testid="new-chat-start-btn"
            size="sm"
            onClick={handleStart}
            disabled={!isValid}
            className="h-9 text-sm px-4"
          >
            Start Conversation
          </Button>
        </div>
      </div>
    </div>
  );
}
